import React, { useState } from "react";
import { PURPOSE_OPTIONS, HOUR_BUCKETS, HOURS_BUDGET } from "./data.js";

export default function DirectionStage({ onDeploy }) {
  const [purposeId, setPurposeId] = useState(null);
  const [purposeConfirmed, setPurposeConfirmed] = useState(false);
  const [hours, setHours] = useState(Object.fromEntries(HOUR_BUCKETS.map((b) => [b.id, 0])));

  const chosenPurpose = PURPOSE_OPTIONS.find((p) => p.id === purposeId);
  const setHour = (id, value) => setHours((prev) => ({ ...prev, [id]: Math.max(0, value) }));

  const totalHours = Object.values(hours).reduce((s, v) => s + v, 0);
  const remaining = HOURS_BUDGET - totalHours;
  const canDeploy = purposeConfirmed && remaining === 0;

  const handleDeploy = () => {
    if (!canDeploy) return;
    onDeploy({ purposeQuality: chosenPurpose.quality, hours });
  };

  if (!purposeConfirmed) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
          <span>DEFINITE PURPOSE</span>
        </div>

        <p className="mb-5 text-xs text-mist/60">
          Purpose isn't a feeling — it's direction specific enough to tell you what to ignore.
          Pick the statement that would actually work.
        </p>

        <div className="mb-6 space-y-2">
          {PURPOSE_OPTIONS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPurposeId(p.id)}
              className={`w-full border-2 px-4 py-3 text-left text-xs transition-colors ${
                purposeId === p.id ? "border-cyan bg-cyan/10 text-cyan" : "border-mist/30 text-mist/80 hover:border-cyan"
              }`}
            >
              {p.text}
            </button>
          ))}
        </div>

        {purposeId && (
          <div
            className={`animate-popIn mb-4 border-2 p-4 text-center ${
              chosenPurpose.quality === "sharp" ? "border-lime bg-lime/10 text-lime" : "border-gold bg-gold/10 text-gold"
            }`}
          >
            <p className="text-xs">
              {chosenPurpose.quality === "sharp"
                ? "Specific and dated — this is direction, not just motivation. It tells you exactly what counts as progress."
                : "Not wrong, just vague — nothing here tells you what to do on a random Tuesday. A sharper purpose names the thing and the deadline."}
            </p>
          </div>
        )}

        {purposeId && (
          <button onClick={() => setPurposeConfirmed(true)} className="btn-magenta w-full">
            LOCK IN THIS PURPOSE ▶
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>OFF-THE-CLOCK HOURS</span>
        <span>BUDGET {HOURS_BUDGET} HRS/WEEK</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Same principle as money: every hour needs a job, or it just leaks away into whatever's
        easiest. Allocate the full {HOURS_BUDGET} hours.
      </p>

      <div
        className={`mb-6 border-2 p-4 text-center transition-colors ${
          remaining === 0 ? "border-lime bg-lime/10" : remaining > 0 ? "border-gold bg-gold/10" : "border-danger bg-danger/10"
        }`}
      >
        <p className="label-pixel mb-1 text-mist/60">Hours Remaining</p>
        <p className={`font-display text-xl ${remaining === 0 ? "text-lime" : remaining > 0 ? "text-gold" : "text-danger"}`}>
          {remaining} hrs
        </p>
      </div>

      <div className="mb-6 space-y-3">
        {HOUR_BUCKETS.map((b) => (
          <div key={b.id} className="panel p-4">
            <p className="mb-1 font-display text-[10px] text-mist">
              {b.icon} {b.label}
            </p>
            <p className="mb-3 text-[11px] text-mist/50">{b.flavor}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setHour(b.id, hours[b.id] - 1)}
                className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist"
              >
                −
              </button>
              <span className="w-16 text-center font-mono text-sm text-mist">{hours[b.id]} hrs</span>
              <button
                onClick={() => setHour(b.id, hours[b.id] + 1)}
                className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist"
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
        {canDeploy ? "START THE 90 DAYS ▶" : "ALLOCATE ALL HOURS TO CONTINUE"}
      </button>
    </div>
  );
}
